/**
 * PHASE 11 — diagnostics overlay.
 *
 * Enabled with `?diag=1`. Renders a live readout of the sensor pipeline,
 * time/location, astronomy context, per-frame timing and the satellite
 * data-source status. It is a dev/field-verification tool: it never ships
 * in the default UI and updates at a throttled rate (perf: no DOM writes
 * in the hot path).
 */

import type { SatelliteProvider } from "../catalog/satellites";
import { satelliteDataSourceReport } from "../catalog/satellites";
import type { BodyRenderState } from "../render/dynamicBodies";

export interface BodyDiagnostics {
  name: string;
  altitudeDeg: number;
  azimuthDeg: number;
  xPx: number | null;
  yPx: number | null;
  clipW: number | null;
  inFrustum: boolean;
}

export interface DiagnosticsSnapshot {
  orientationStatus: string;
  headingDeg: number | null;
  tiltDeg: number | null;
  qCamera: string | null;
  calibrationOffsetDeg: number;
  latDeg: number | null;
  lonDeg: number | null;
  utcMs: number;
  fovYDeg: number;
  arMode: boolean;
  framesDrawn: number;
  frameMs: number;
  lastFrameMs: number;
  satelliteReport: string;
  bodies: readonly BodyRenderState[];
  /** Bodies currently above the horizon (diagnostics convenience). */
  bodiesVisible: readonly string[];
  // --- forensic extensions (all optional for backward compat) ---
  orientationSource?: string;
  qCameraNull?: boolean;
  fallbackActive?: boolean;
  fallbackReason?: string | null;
  simActive?: boolean;
  bodyCountGpu?: number;
  bodiesExtended?: readonly BodyDiagnostics[];
  centeredBody?: string | null;
  centeredScreen?: { xPx: number; yPx: number; clipW: number; inFrustum: boolean } | null;
  canvasWidthPx?: number;
  canvasHeightPx?: number;
}

export const DIAGNOSTICS_UPDATE_MS = 500;

export class DiagnosticsOverlay {
  private el: HTMLElement;
  private lastRenderMs = -Infinity;

  constructor(root: HTMLElement) {
    this.el = root;
  }

  get visible(): boolean {
    return this.el.style.display !== "none";
  }

  show(): void {
    this.el.style.display = "block";
  }

  hide(): void {
    this.el.style.display = "none";
  }

  /** Throttled render of the snapshot into the panel. */
  sync(snapshot: DiagnosticsSnapshot): void {
    const now = performance.now();
    if (now - this.lastRenderMs < DIAGNOSTICS_UPDATE_MS) return;
    this.lastRenderMs = now;

    const lines: string[] = [
      `orientation: ${snapshot.orientationStatus}`,
      `heading: ${snapshot.headingDeg === null ? "n/a" : `${snapshot.headingDeg.toFixed(1)}°`}`,
      `tilt: ${snapshot.tiltDeg === null ? "n/a" : `${snapshot.tiltDeg.toFixed(1)}°`}`,
      `calibration offset: ${snapshot.calibrationOffsetDeg.toFixed(1)}°`,
      `location: ${snapshot.latDeg === null ? "n/a" : `${snapshot.latDeg.toFixed(4)}, ${snapshot.lonDeg?.toFixed(4)}`}`,
      `utc: ${new Date(snapshot.utcMs).toISOString()}`,
      `fov: ${snapshot.fovYDeg.toFixed(0)}° (${snapshot.arMode ? "AR" : "sky"})`,
      `frames: ${snapshot.framesDrawn} @ ${snapshot.frameMs.toFixed(1)} ms/frame (last ${snapshot.lastFrameMs.toFixed(1)})`,
      `satellites: ${snapshot.satelliteReport}`,
      `bodies: ${snapshot.bodiesVisible.length ? snapshot.bodiesVisible.join(", ") : "none visible"}`,
    ];
    if (snapshot.orientationSource !== undefined) lines.push(`source: ${snapshot.orientationSource}`);
    if (snapshot.qCameraNull !== undefined) lines.push(`qCamera: ${snapshot.qCameraNull ? "NULL (frozen sky)" : "ok"}`);
    if (snapshot.simActive !== undefined) lines.push(`simActive: ${snapshot.simActive ? "YES (simulated)" : "NO (real sensors)"}`);
    if (snapshot.fallbackActive !== undefined) lines.push(`fallback: ${snapshot.fallbackActive ? `ACTIVE (${snapshot.fallbackReason ?? "unknown"})` : "none"}`);
    if (snapshot.bodyCountGpu !== undefined) lines.push(`bodies GPU: ${snapshot.bodyCountGpu}`);
    if (snapshot.canvasWidthPx !== undefined) lines.push(`canvas: ${snapshot.canvasWidthPx}×${snapshot.canvasHeightPx}`);
    if (snapshot.centeredBody) {
      lines.push(`CENTER DIAG: ${snapshot.centeredBody}`);
      if (snapshot.centeredScreen) {
        lines.push(
          ` center screen: x=${snapshot.centeredScreen.xPx.toFixed(0)} y=${snapshot.centeredScreen.yPx.toFixed(0)} clipW=${snapshot.centeredScreen.clipW.toFixed(3)} inFrustum=${snapshot.centeredScreen.inFrustum ? "YES" : "NO"}`,
        );
      }
    }
    // Prefer extended per-body diagnostics when available (includes screen/clip)
    if (snapshot.bodiesExtended && snapshot.bodiesExtended.length > 0) {
      for (const b of snapshot.bodiesExtended) {
        const screen = b.xPx !== null && b.yPx !== null ? ` x=${b.xPx.toFixed(0)} y=${b.yPx.toFixed(0)}` : " x=n/a y=n/a";
        const clip = b.clipW !== null ? ` w=${b.clipW.toFixed(3)}` : " w=n/a";
        lines.push(
          `${b.name.padEnd(8)} az ${b.azimuthDeg.toFixed(0).padStart(3)} alt ${b.altitudeDeg.toFixed(0).padStart(3)}${screen}${clip} frustum=${b.inFrustum ? "YES" : "NO"}`,
        );
      }
    } else {
      for (const body of snapshot.bodies) {
        lines.push(
          `${body.name.padEnd(8)} az ${body.azimuthDeg.toFixed(0).padStart(3)} alt ${body.altitudeDeg.toFixed(0).padStart(3)} mag ${body.magnitude.toFixed(1)}`,
        );
      }
    }
    this.el.replaceChildren(document.createTextNode(lines.join("\n")));
  }

  /** Static factory for the report line (used by tests). */
  static satelliteReport(provider: SatelliteProvider): string {
    return satelliteDataSourceReport(provider);
  }
}
